import { FieldKey, OptionKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { CheckboxFormField, FormField, FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { BuyerFormsSyncActions, FormFieldSyncActions } from '@core/data-access/store/form/form.actions';
import { setFormFields } from '@core/data-access/store/form/form.reducer';
import { AnonymousVoucherActions } from '@core/data-access/store/shared/shared.actions';
import { BuyerInformationStoreState } from '@core/features/buyer-information/models/buyer-information-store.model';
import {
  BuyerBillingAddressFormActions,
  BuyerInformationActions,
  BuyerInformationFormActions,
  BuyerPrivacyPolicyFormActions
} from '@core/features/buyer-information/store/buyer-information.actions';
import { createReducer, on } from '@ngrx/store';
import { UpdateProfileBillingAddress } from '@store/user/user.actions';
import { isEqual, isNil, omit, pickBy, reduce } from 'lodash';

export const buyerInformationReducerKey = 'buyerInformation';

const initialBuyerInformationStoreState: BuyerInformationStoreState = {
  person: null,
  forms: {
    [FormName.BuyerInformation]: { isPrefilled: null, formFields: {} },
    [FormName.BuyerBillingAddress]: { selectedSavedBillingAddressId: null, formFields: {} },
    [FormName.BuyerPrivacyPolicy]: { formFields: {} }
  }
};

export const buyerInformationReducer = createReducer(
  initialBuyerInformationStoreState,
  on(BuyerInformationActions.setBuyerPerson, (state, { person }) => {
    if (isEqual(state.person, person)) {
      return state;
    }

    return { ...state, person };
  }),
  on(BuyerInformationActions.removeBuyerPerson, (state) => ({ ...state, buyerUuid: null })),
  on(FormFieldSyncActions.setBuyerInformationFormFields, (state, { formFields }) =>
    setFormFields(state, formFields, FormName.BuyerInformation)
  ),
  on(
    BuyerInformationFormActions.setBuyerInformationPrefillAndFormFields,
    (state, { prefillBuyerInformationForm, person: savedBuyerPerson, formFields }) => {
      const { person: buyerPerson, forms } = state;

      const person = !prefillBuyerInformationForm ? buyerPerson : savedBuyerPerson;

      const updateFormFields = !prefillBuyerInformationForm ? forms[FormName.BuyerInformation].formFields : formFields;
      const removedQuestionnaireAndCreateAccountCheckboxFormFields = pickBy(
        omit(updateFormFields, FieldKey.CreateAccount),
        ({ isQuestionnaire }) => !isQuestionnaire
      );

      return {
        ...state,
        person,
        forms: {
          ...forms,
          [FormName.BuyerInformation]: {
            ...forms[FormName.BuyerInformation],
            formFields: removedQuestionnaireAndCreateAccountCheckboxFormFields,
            isPrefilled: prefillBuyerInformationForm
          }
        }
      };
    }
  ),
  on(
    BuyerBillingAddressFormActions.setSelectedSavedBillingAddressIdAndFormFields,
    (state, { selectedSavedBillingAddress }) => {
      const { forms } = state;

      const selectedSavedBillingAddressId = selectedSavedBillingAddress.id;
      const selectedSavedBillingAddressFormFields = omit(selectedSavedBillingAddress, 'id');

      const isSavedBillingAddressSelected = !isNil(selectedSavedBillingAddressId);

      const formFields = reduce(
        selectedSavedBillingAddressFormFields,
        (formFields, value, key) => {
          formFields[key] = {
            value,
            isDisabled: isSavedBillingAddressSelected,
            isQuestionnaire: false
          } as FormField;

          return formFields;
        },
        {} as FormFields
      );

      const saveAddressCheckbox: CheckboxFormField = {
        isHidden: isSavedBillingAddressSelected,
        isDisabled: isSavedBillingAddressSelected,
        isQuestionnaire: false,
        value: {
          [OptionKey.SaveBillingAddress]: {
            value: false,
            isDisabled: isSavedBillingAddressSelected,
            isHidden: isSavedBillingAddressSelected,
            isQuestionnaire: isSavedBillingAddressSelected
          }
        }
      };

      formFields[FieldKey.SaveAddress] = saveAddressCheckbox;

      return {
        ...state,
        forms: {
          ...forms,
          [FormName.BuyerBillingAddress]: {
            ...forms[FormName.BuyerBillingAddress],
            formFields,
            selectedSavedBillingAddressId
          }
        }
      };
    }
  ),
  on(UpdateProfileBillingAddress, (state, updatedSavedBillingAddress) => {
    const { forms } = state;
    const buyerBillingAddressForm = forms[FormName.BuyerBillingAddress];

    const isSelectedSavedBillingAddressUpdated =
      buyerBillingAddressForm.selectedSavedBillingAddressId === updatedSavedBillingAddress.id;

    if (!isSelectedSavedBillingAddressUpdated) {
      return state;
    }

    const updatedSavedBillingAddressFormFields = omit(updatedSavedBillingAddress, 'type', 'delete', 'id', 'label');

    const formFields = reduce(
      updatedSavedBillingAddressFormFields,
      (formFields, value, key) => {
        formFields[key] = {
          value,
          isDisabled: true,
          isQuestionnaire: false
        } as FormField;

        return formFields;
      },
      {} as FormFields
    );

    return {
      ...state,
      forms: {
        ...forms,
        [FormName.BuyerBillingAddress]: {
          ...buyerBillingAddressForm,
          formFields,
          selectedSavedBillingAddress: updatedSavedBillingAddress.id
        }
      }
    };
  }),
  on(FormFieldSyncActions.setBuyerBillingAddressFormFields, (state, { formFields }) =>
    setFormFields(state, formFields, FormName.BuyerBillingAddress)
  ),
  on(BuyerPrivacyPolicyFormActions.setPrivacyPolicyCheckbox, (state, { formFields }) =>
    setFormFields(state, formFields, FormName.BuyerPrivacyPolicy)
  ),
  on(BuyerFormsSyncActions.createTicketHolderBuyerPerson, (state, { createdPerson, formFields }) => {
    const updatedState = setFormFields(state, formFields, FormName.BuyerInformation);

    return {
      ...updatedState,
      person: createdPerson
    };
  }),
  on(BuyerFormsSyncActions.changeBuyerInformationPersonBackToSavedBuyerPerson, (state, { savedPerson, formFields }) => {
    const updatedState = setFormFields(state, formFields, FormName.BuyerInformation);

    return {
      ...updatedState,
      person: savedPerson
    };
  }),
  on(
    AnonymousVoucherActions.resetStateOnAnonymousVoucherRedeem,
    BuyerInformationActions.resetReducer,
    () => initialBuyerInformationStoreState
  )
);
